import React, { useEffect } from "react";
import { Outlet, useMatch, useNavigate } from "react-router";
import { addGoogleAnalyticsScripts } from "../../functions/scriptUtils";
import Loading from "../common/Loading";
import WrapperCard from "../common/WrapperCard";
import GenericError from "../pages/GenericError";
import { AppContext } from "./ApplicationContextProvider";
import type { BusinessLineProps } from "./types";
import type { AppContextType } from "./types";

export function BusinessLine(props: BusinessLineProps) {
  const { isTokenLoaded, loadToken, details, loader, applicationError } =
    React.useContext(AppContext) as AppContextType;

  const route = useMatch("/:businessLine/token/:token/*");
  const token = route?.params?.["token"];
  const routeBusinessLine = route?.params?.["businessLine"];
  const businessLine =
    details?.businessLineDetails?.businessLine?.toLowerCase();
  const needsAuthentication = details?.needsAuthentication;
  const { environment } = props;
  const navigate = useNavigate();

  useEffect(() => {
    if (!businessLine || !environment) return;

    const gtag = environment.googleAnalytics?.[businessLine];
    if (gtag) {
      addGoogleAnalyticsScripts(gtag);
    }
  }, [businessLine, environment]);

  useEffect(() => {
    if (!isTokenLoaded) {
      loadToken(token);
    }
  }, [token, isTokenLoaded, loadToken]);

  useEffect(() => {
    if (!businessLine || !routeBusinessLine) return;
    runRedirects();
  }, [businessLine, needsAuthentication, routeBusinessLine, details, applicationError, navigate]);

  const urlBusinessLineMatchesToken = () => {
    if (!businessLine || !routeBusinessLine) return false;
    return businessLine.toLowerCase() === routeBusinessLine.toLowerCase();
  };

  const runRedirects = () => {
    if (!token || !details) return;

    if (!urlBusinessLineMatchesToken()) {
      navigate(`/${businessLine}/token/${token}`, { replace: true });
    } else if (applicationError) {
      navigate("custom-error", { replace: true });
    } else if (details.completed) {
      navigate("success", { replace: true });
    } else if (details.expired) {
      navigate("token-expired", { replace: true });
    } else if (
      needsAuthentication &&
      details.campaignDetails?.authenticationPage
    ) {
      navigate("authentication", { replace: true });
    } else if (details.campaignDetails?.name) {
      navigate(`${details.campaignDetails.name.toLowerCase()}/complete`, {
        replace: true,
      });
    }
  };

  const renderContent = () => {
    return (
      <div>
        <Outlet />
      </div>
    );
  };

  return (
    <div className="app-display">
      {loader.loading ? (
        <WrapperCard>
          <Loading />
        </WrapperCard>
      ) : loader.error ? (
        <GenericError />
      ) : (
        details && renderContent()
      )}
    </div>
  );
}
